/**
 * Générateur — O11 : résoudre des problèmes en utilisant la proportionnalité
 * dans le cadre de la géométrie (agrandissement / réduction).
 * Attendu ID : 174 | Niveau : 4ème | Domaine : proportionnalite
 *
 * Cœur : le triangle AB′C′ est un agrandissement ou une réduction du triangle
 * ABC ; les longueurs sont multipliées par un même nombre k, le coefficient
 * (k > 1 : agrandissement, k < 1 : réduction). Longueurs de ABC entières de 2 à
 * 12 cm, triangle non aplati à côtés distincts ; toutes les valeurs exactes par
 * construction (rationnels de proportionnalite.js), recalculées par un second
 * chemin dans le gen (throw sinon). Aucun solveur test-side ne couvre 174 ; le
 * test jetable tmp-revue-174 relit longueurs, coefficient et figure.
 * ⚠️ Décision utilisatrice (juillet) : effets k² et k³ sur les aires et les
 * volumes EXCLUS (attendu « grandeurs »).
 *
 * Revue 2026-09-23 (vague 3), décisions du coordinateur appliquées en mode
 * auto. Attendu de fin de 4e (Eduscol, p. 6) : « Il résout des problèmes en
 * utilisant la proportionnalité dans le cadre de la géométrie. » Exemple de
 * réussite : « Dans le cadre d'un agrandissement-réduction ou dans une
 * configuration de Thalès, il sait calculer une longueur manquante en
 * utilisant la proportionnalité. » Cours (19Thales4 p. 5) : « Le triangle
 * AB′C′ est une réduction du triangle ABC. Les longueurs du triangle ABC sont
 * multipliées par un même nombre k appelé le coefficient de réduction »,
 * « AB′ = k × AB », k = AB′/AB ; la figure est celle des triangles EMBOÎTÉS.
 *   - Mot unique « coefficient » (coefficient d'agrandissement, coefficient
 *     de réduction), plus jamais « rapport » ; « agrandissement » ou
 *     « réduction » dit selon k (avant : « rapport d'agrandissement » pour une
 *     réduction dans 60,4 % des tirages, « un agrandissement (ou une
 *     réduction) » dans 100 %).
 *   - Figure : les triangles emboîtés du cours (svgTrianglesEmboites), sommet
 *     commun A, B′ sur la droite (AB), C′ sur la droite (AC), (B′C′) parallèle à
 *     (BC), lettres en KaTeX_Math italique, trois gabarits ; AUCUNE longueur
 *     sur la figure (les longueurs sont dans l'énoncé : plus de chevauchement
 *     d'étiquettes, 8,4 % avant) ; le petit triangle est dessiné à un
 *     coefficient éloigné d'au moins 0,15 du vrai, avec « La figure n'est pas
 *     à l'échelle. » ; `alt` qui décrit sans donner de réponse. La
 *     configuration papillon (3e) n'est jamais dessinée. Mode auto, À
 *     ARBITRER : le BO 2025 place Thalès et les triangles emboîtés en 3e, le
 *     cours du prof et l'attendu 2019 les mettent en 4e.
 *   - Rappel brouillon IMPÉRATIF sur tous les formats (« … pour résoudre. »,
 *     attendu de problèmes), juste avant la question.
 *   - Exemple sacrifié du coup de pouce prévu au cadrage (7 cm, k = 1,5,
 *     10,5 cm) : aucune longueur de 7 cm quand k = 1,5.
 *   - rapport_texte GARDÉ, libellé « Coefficient (saisie) » : la consigne
 *     impose « sous forme décimale » et k est décimal exact (la fraction 3/4
 *     était refusée dans 71,5 % des tirages) ; « (I′J′ ÷ IJ) » retiré ; le
 *     côté donné change (AB, AC ou BC).
 *   - rapport_fraction_texte_trou SORTI par FUSION avec rapport_texte (branche
 *     prévue par les décisions) : sans « irréductible » et avec une seule
 *     réponse (« Coefficient = 6 ÷ 4 = … »), la carte ne demandait plus
 *     qu'une division déjà posée, le calcul de rapport_texte avec la méthode
 *     écrite.
 *   - longueur_texte GARDÉ, libellé « Longueur manquante (saisie) » : la
 *     transformation est nommée ; trois sens tirés à parts égales, image
 *     (k × longueur), longueur d'origine (image ÷ k) et k NON donné (deux
 *     longueurs qui se correspondent et une troisième, l'exemple de réussite).
 *   - agrandissement_qcm_unique RECADRÉ, libellé « Coefficient ou longueur
 *     (QCM) » : le QCM de l'attendu. Longueur manquante trois fois sur quatre
 *     (agrandissement ou réduction), distracteurs additif (la principale,
 *     DEPP q. 4 : l'écart des deux côtés ajouté), coefficient_inverse (÷ k),
 *     somme (les trois longueurs de l'énoncé additionnées) ; coefficient une
 *     fois sur quatre, en agrandissement seulement (mode auto, À ARBITRER : en
 *     réduction, coefficient_inverse et somme dépassent toujours 1, additif
 *     dès que les deux côtés diffèrent d'un centimètre ou plus, et la clé
 *     serait la seule option inférieure à 1 dans 94,4 % des configurations
 *     possibles). « Ni un agrandissement ni une réduction »
 *     sort. Contrat exporté. Ajout du correcteur : le rang de la clé parmi les
 *     quatre valeurs est visé uniformément parmi ceux que les familles
 *     permettent (sans cela deuxième dans 58 % des tirages, jamais la plus
 *     grande).
 *   - tableau_texte_trou GARDÉ : le tableau des longueurs des deux triangles,
 *     une colonne complète, k donné un tirage sur deux seulement ; phrase de
 *     méthode retirée ; étiquettes-pièges additif (principale),
 *     coefficient_inverse, recopie ; au moins deux affectations respectent
 *     l'ordre des longueurs.
 *   - geo_tableau_vf RECADRÉ : quatre lignes (avant cinq), « Les angles sont
 *     conservés » SORT (absent du cours) ; une ligne de propriété au plus,
 *     vraie ou fausse ; lignes chiffrées variées (image, longueur d'origine,
 *     périmètre) et formule « B′C′ = k × BC », chacune tirée vraie ou fausse
 *     (fausses : coefficient_inverse, additif) ; une à trois vraies.
 *   - longueur_relier GARDÉ : exactement deux leurres (trois dans 59,3 %),
 *     additif_rapport et coefficient_inverse, jamais l'étiquette d'une source ;
 *     un leurre au moins entre les vraies cibles ; « Deux longueurs sont en
 *     trop. ».
 * Six formats (avant : sept).
 */

import {
    randomChoice, randInt, shuffle, filtrerDistracteurs,
    makeSaisie, makeTexteTrou, makeQcm, makeVraiFaux, makeRelier, tex,
} from '../../../utils';
import {
    ratFromStr, ratMul, ratDiv, ratAdd, ratSub, ratEq, ratN, ratToDecFr, ratVal, nbDecimales,
    formatFr, decTex, tableEnonce, ligneTable, imgElement, svgTrianglesEmboites,
} from './proportionnalite';

const ATTENDU_ID = 174;
const L_AGRANDISSEMENT = 'Proportionnalité en géométrie (agrandissement-réduction)';
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (décision 2026-09-23), IMPÉRATIF, tournure des attendus de
// problèmes (149, 157) : k × L et L′ ÷ L en décimaux se posent à la main.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon pour résoudre.</p>';
const PAS_A_L_ECHELLE = '<p>La figure n\'est pas à l\'échelle.</p>';

const R = (s) => ratFromStr(String(s));
const nb = (r) => formatFr(ratToDecFr(r));
const T = (s) => `\\text{${s}}`;

const K_AGRANDISSEMENT = ['1,25', '1,5', '1,6', '2', '2,5', '3'];
const K_REDUCTION = ['0,4', '0,5', '0,6', '0,75', '0,8'];

// Côtés de ABC et leurs images dans AB′C′ (A est son propre correspondant).
const COTES = ['AB', 'AC', 'BC'];
const IMAGE = { AB: 'AB\'', AC: 'AC\'', BC: 'B\'C\'' };
/** « AB = 4 » en KaTeX (lettres italiques, virgule protégée). */
const egal = (seg, v) => tex(`${seg} = ${decTex(nb(v))}`);
const TRI_ABC = tex('ABC');
const TRI_IMAGE = tex('AB\'C\'');
const nature = (red) => (red ? 'une réduction' : 'un agrandissement');
const coefficientDe = (red) => (red ? 'coefficient de réduction' : 'coefficient d\'agrandissement');

/** Triangle ABC : côtés entiers de 2 à 12, distincts, triangle non aplati. */
const tirerTriangle = () => {
    for (let essai = 0; essai < 100; essai++) {
        const [a, b, c] = [randInt(2, 12), randInt(2, 12), randInt(2, 12)];
        if (new Set([a, b, c]).size === 3 && a + b > c && a + c > b && b + c > a) return { AB: ratN(a), AC: ratN(b), BC: ratN(c) };
    }
    return { AB: ratN(4), AC: ratN(5), BC: ratN(6) };
};

/** k et le sens, avec images exactes à deux décimales au plus ; jamais 7 cm avec k = 1,5. */
const tirerConfiguration = (reduction, pool) => {
    for (let essai = 0; essai < 100; essai++) {
        const k = R(randomChoice(pool || (reduction ? K_REDUCTION : K_AGRANDISSEMENT)));
        const tri = tirerTriangle();
        const img = Object.fromEntries(COTES.map((c) => [c, ratMul(k, tri[c])]));
        if (COTES.some((c) => nbDecimales(img[c]) > 2)) continue;
        if (ratEq(k, R('1,5')) && COTES.some((c) => ratEq(tri[c], ratN(7)))) continue;   // exemple du coup de pouce
        return { k, tri, img };
    }
    throw new Error('gen_174 : aucune configuration');
};

/** La figure des triangles emboîtés, jamais au coefficient de l'énoncé. */
const figure = (reduction, k) => {
    const vrai = Math.min(ratVal(k), 1 / ratVal(k));
    const ratio = randomChoice([0.45, 0.55, 0.65, 0.75].filter((r) => Math.abs(r - vrai) >= 0.149));
    const alt = reduction
        ? 'Deux triangles emboîtés de sommet commun A : le grand triangle ABC et le petit triangle AB′C′, avec B′ sur le segment [AB], C′ sur le segment [AC] et (B′C′) parallèle à (BC).'
        : 'Deux triangles emboîtés de sommet commun A : le petit triangle ABC et le grand triangle AB′C′, avec B sur le segment [AB′], C sur le segment [AC′] et (BC) parallèle à (B′C′).';
    return { el: imgElement(svgTrianglesEmboites({ reduction, ratioDessin: ratio, forme: randInt(0, 2) }), 'small', alt), ratio };
};

// ═══════════════════════════════════════════════════════════
// 1. SAISIE — le coefficient, sous forme décimale
// ═══════════════════════════════════════════════════════════

const genRapportSaisie = () => {
    const reduction = randInt(0, 1) === 0;
    const { k, tri, img } = tirerConfiguration(reduction);
    const c = randomChoice(COTES);
    // second chemin : k = image ÷ longueur d'origine
    if (!ratEq(ratDiv(img[c], tri[c]), k) || ratToDecFr(k) === null) throw new Error('gen_174 coefficient : incohérent');
    const fig = figure(reduction, k);
    const payload = makeSaisie(ratToDecFr(k),
        `<p>Le triangle ${TRI_IMAGE} est ${nature(reduction)} du triangle ${TRI_ABC}. On a ${egal(c, tri[c])} cm et ${egal(IMAGE[c], img[c])} cm.</p>`
        + PAS_A_L_ECHELLE
        + RAPPEL_BROUILLON
        + `<p><b>Quel est le ${coefficientDe(reduction)} ? Donne-le sous forme décimale.</b></p>`,
        { ...opts(L_AGRANDISSEMENT), strict: false, elements_additionnels: fig.el });
    payload._meta.params = { reduction, k: nb(k), cote: c, ratioDessin: fig.ratio };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 2. SAISIE — une longueur manquante (image, origine, ou k non donné)
// ═══════════════════════════════════════════════════════════

const genLongueurSaisie = () => {
    const reduction = randInt(0, 1) === 0;
    const sens = randomChoice(['image', 'origine', 'sans_k']);
    const { k, tri, img } = tirerConfiguration(reduction);
    const [c1, c2] = shuffle(COTES).slice(0, 2);
    let enonce; let question; let reponse;
    if (sens === 'image') {
        enonce = `Le triangle ${TRI_IMAGE} est ${nature(reduction)} du triangle ${TRI_ABC} de coefficient ${tex(`k = ${decTex(nb(k))}`)}. On a ${egal(c1, tri[c1])} cm.`;
        question = `Quelle est la longueur ${tex(IMAGE[c1])} ?`;
        reponse = img[c1];
    } else if (sens === 'origine') {
        enonce = `Le triangle ${TRI_IMAGE} est ${nature(reduction)} du triangle ${TRI_ABC} de coefficient ${tex(`k = ${decTex(nb(k))}`)}. On a ${egal(IMAGE[c1], img[c1])} cm.`;
        question = `Quelle est la longueur ${tex(c1)} ?`;
        reponse = tri[c1];
    } else {
        enonce = `Le triangle ${TRI_IMAGE} est ${nature(reduction)} du triangle ${TRI_ABC}. On a ${egal(c1, tri[c1])} cm, ${egal(IMAGE[c1], img[c1])} cm et ${egal(c2, tri[c2])} cm.`;
        question = `Quelle est la longueur ${tex(IMAGE[c2])} ?`;
        reponse = img[c2];
    }
    // second chemin : produit en croix sur la paire donnée
    const verif = sens === 'image' ? ratMul(k, tri[c1]) : sens === 'origine' ? ratDiv(img[c1], k) : ratDiv(ratMul(tri[c2], img[c1]), tri[c1]);
    if (!ratEq(verif, reponse)) throw new Error('gen_174 longueur : incohérente');
    const fig = figure(reduction, k);
    const payload = makeSaisie(nb(reponse),
        `<p>${enonce}</p>` + PAS_A_L_ECHELLE + RAPPEL_BROUILLON + `<p><b>${question}</b></p>`,
        { ...opts(L_AGRANDISSEMENT), strict: false, suffixe: 'cm', elements_additionnels: fig.el });
    payload._meta.params = { reduction, sens, k: nb(k), ratioDessin: fig.ratio };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 3. QCM — la longueur manquante, ou le coefficient
// ═══════════════════════════════════════════════════════════

export const REGLES_DISTRACTEURS_QCM = {
    agrandissement_qcm_unique: {
        support: 'le triangle AB′C′ est un agrandissement ou une réduction du triangle ABC (côtés entiers de 2 à 12 cm, distincts) ; trois fois sur quatre, deux côtés qui se correspondent (L, L′) et un troisième côté M de ABC sont donnés, on cherche son image k × M ; une fois sur quatre, en agrandissement seulement, on cherche le coefficient k = L′ ÷ L',
        familles: {
            additif: 'longueur : M + (L′ − L), l\'écart des deux côtés reporté au lieu du coefficient (DEPP 4e 2023 q. 4, 15,1 % ; principale, toujours présente) ; coefficient : L′ − L, l\'écart pris pour le coefficient',
            coefficient_inverse: 'longueur : M ÷ k (le coefficient appliqué à l\'envers) ; coefficient : L ÷ L′',
            somme: 'longueur : L + L′ + M ; coefficient : L + L′ (les nombres de l\'énoncé additionnés, DEPP q. 5)',
        },
        supprimees: {
            ni_l_un_ni_l_autre: '« Ni un agrandissement ni une réduction » : jamais juste, sans erreur derrière',
            agrandissement_ou_reduction: 'l\'ancienne question « agrandissement ou réduction ? » : la figure à l\'échelle donnait la réponse dans 100 % des tirages',
        },
        garantie: 'quatre choix deux à deux distincts, tous positifs et décimaux exacts à deux décimales au plus : additif ≠ clé car L ≠ M (longueur) ; coefficient_inverse ≠ clé car k ≠ 1 ; toute égalité fortuite écartée par comparaison exacte, nouveau tirage borné ; figure au coefficient faux, mention « pas à l\'échelle »',
    },
};

// Rang de la clé parmi les quatre valeurs, visé uniformément parmi ceux que les
// familles permettent : sans cela elle était deuxième dans 58 % des tirages et
// jamais la plus grande. Longueur : rang 1 à 4 (réduction : additif au-dessous
// de la clé si M < L, les deux autres au-dessus ; agrandissement :
// coefficient_inverse au-dessous, somme au-dessous seulement quand k et M sont
// grands) ; coefficient : rang 2 ou 3 (1 ÷ k est toujours au-dessous).
const genAgrandissementQcm = () => {
    const surCoefficient = randInt(0, 3) === 0;
    const rangVise = surCoefficient ? randInt(2, 3) : randInt(1, 4);
    for (let essai = 0; essai < 300; essai++) {
        const reduction = surCoefficient ? false : randInt(0, 1) === 0;
        const pool = surCoefficient ? ['1,25', '1,6', '2', '2,5'] : null;   // 1 ÷ k décimal exact
        const { k, tri, img } = tirerConfiguration(reduction, pool);
        const [c1, c2] = shuffle(COTES).slice(0, 2);
        const L = tri[c1]; const Lp = img[c1]; const M = tri[c2];
        let cle; let familles; let enonce; let question; let unite;
        if (surCoefficient) {
            cle = k;
            familles = { additif: ratSub(Lp, L), coefficient_inverse: ratDiv(L, Lp), somme: ratAdd(L, Lp) };
            enonce = `Le triangle ${TRI_IMAGE} est un agrandissement du triangle ${TRI_ABC}. On a ${egal(c1, L)} cm et ${egal(IMAGE[c1], Lp)} cm.`;
            question = 'Quel est le coefficient d\'agrandissement ?';
            unite = '';
            if (!ratEq(ratDiv(Lp, L), cle)) throw new Error('gen_174 QCM : coefficient incohérent');
        } else {
            cle = img[c2];
            familles = { additif: ratAdd(M, ratSub(Lp, L)), coefficient_inverse: ratDiv(M, k), somme: ratAdd(ratAdd(L, Lp), M) };
            enonce = `Le triangle ${TRI_IMAGE} est ${nature(reduction)} du triangle ${TRI_ABC}. On a ${egal(c1, L)} cm, ${egal(IMAGE[c1], Lp)} cm et ${egal(c2, M)} cm.`;
            question = `Quelle est la longueur ${tex(IMAGE[c2])} ?`;
            unite = ' cm';
            if (!ratEq(ratDiv(ratMul(M, Lp), L), cle)) throw new Error('gen_174 QCM : longueur incohérente');
        }
        const valeurs = [cle, familles.additif, familles.coefficient_inverse, familles.somme];
        if (valeurs.some((v) => ratVal(v) <= 0 || nbDecimales(v) > 2)) continue;
        if (new Set(valeurs.map((v) => ratToDecFr(v))).size !== 4) continue;
        const rang = 1 + valeurs.filter((v) => ratVal(v) < ratVal(cle)).length;
        if (rang !== rangVise && essai < 250) continue;
        const fig = figure(reduction, k);
        const affiche = (v) => `${nb(v)}${unite}`;
        const payload = makeQcm(valeurs.map(affiche), affiche(cle),
            `<p>${enonce}</p>` + PAS_A_L_ECHELLE + RAPPEL_BROUILLON + `<p><b>${question}</b></p>`,
            { ...opts(L_AGRANDISSEMENT), elements_additionnels: fig.el });
        payload._meta.params = {
            question: surCoefficient ? 'coefficient' : 'longueur', reduction, k: nb(k), ratioDessin: fig.ratio,
            distracteurs: ['additif', 'coefficient_inverse', 'somme'],
        };
        return payload;
    }
    throw new Error('gen_174 QCM : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// 4. TABLEAU — les longueurs des deux triangles, deux cases (drag)
// ═══════════════════════════════════════════════════════════

/** Affectations de deux étiquettes aux deux trous qui respectent l'ordre des longueurs de ABC. */
const affectationsOrdonnees = (tri, img, trous, etiquettes) => {
    let n = 0;
    etiquettes.forEach((e1) => etiquettes.forEach((e2) => {
        if (e1 === e2) return;
        const image = { ...img, [trous[0]]: R(e1), [trous[1]]: R(e2) };
        const ok = COTES.every((a) => COTES.every((b) => ratVal(tri[a]) >= ratVal(tri[b]) || ratVal(image[a]) < ratVal(image[b])));
        if (ok) n++;
    }));
    return n;
};

const genTableauTrou = () => {
    for (let essai = 0; essai < 200; essai++) {
        const reduction = randInt(0, 1) === 0;
        const avecK = randInt(0, 1) === 0;
        const { k, tri, img } = tirerConfiguration(reduction);
        const connu = randomChoice(COTES);
        const trous = COTES.filter((c) => c !== connu);
        const mots = trous.map((c) => nb(img[c]));
        // étiquettes-pièges : additif (M + L′ − L), coefficient_inverse (M ÷ k), recopie (M)
        const cand = [];
        trous.forEach((c) => {
            cand.push({ v: ratAdd(tri[c], ratSub(img[connu], tri[connu])), code: 'additif' });
            cand.push({ v: ratDiv(tri[c], k), code: 'coefficient_inverse' });
            cand.push({ v: tri[c], code: 'recopie' });
        });
        const surs = cand.filter((c) => ratVal(c.v) > 0 && nbDecimales(c.v) <= 2 && !ratEq(c.v, img[connu]));
        const phrase = trous.map((c, j) => `$${IMAGE[c]} = {{${mots[j]}}}${T(' cm')}$`).join('\n');
        const textes = filtrerDistracteurs(phrase, surs.map((c) => nb(c.v)));
        const retenus = surs.filter((c, i, arr) => textes.includes(nb(c.v)) && arr.findIndex((d) => nb(d.v) === nb(c.v)) === i);
        const additifs = shuffle(retenus.filter((c) => c.code === 'additif'));
        const autres = shuffle(retenus.filter((c) => c.code !== 'additif'));
        if (!additifs.length || retenus.length < 3) continue;
        const choisis = [additifs[0], ...shuffle([...additifs.slice(1), ...autres]).slice(0, 2)];
        if (choisis.length < 3) continue;
        const distracteurs = choisis.map((c) => nb(c.v));
        if (affectationsOrdonnees(tri, img, trous, [...mots, ...distracteurs]) < 2) continue;
        // second chemin : chaque case = longueur × (image connue ÷ longueur connue)
        trous.forEach((c) => { if (!ratEq(ratDiv(ratMul(tri[c], img[connu]), tri[connu]), img[c])) throw new Error('gen_174 tableau : incohérent'); });
        const tableau = tableEnonce(
            ligneTable(`Triangle ${TRI_ABC}`, COTES.map((c) => `${egal(c, tri[c])} cm`)),
            ligneTable(`Triangle ${TRI_IMAGE}`, COTES.map((c) => (c === connu ? `${egal(IMAGE[c], img[c])} cm` : `${tex(IMAGE[c])} = …`))),
        );
        const fig = figure(reduction, k);
        const payload = makeTexteTrou(phrase,
            `<p>Le triangle ${TRI_IMAGE} est ${nature(reduction)} du triangle ${TRI_ABC}${avecK ? ` de coefficient ${tex(`k = ${decTex(nb(k))}`)}` : ''}.</p>\n${tableau}`
            + PAS_A_L_ECHELLE + RAPPEL_BROUILLON
            + `<p><b>Complète les deux longueurs manquantes du triangle ${TRI_IMAGE}.</b></p>`,
            { ...opts(L_AGRANDISSEMENT), mode: 'drag', distracteurs, elements_additionnels: fig.el });
        payload._meta.params = { reduction, avecK, k: nb(k), connu, ratioDessin: fig.ratio, distracteurs: choisis.map((c) => c.code) };
        return payload;
    }
    throw new Error('gen_174 tableau : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// 5. VRAI/FAUX — quatre affirmations, une ligne de propriété au plus
// ═══════════════════════════════════════════════════════════

const genGeoVF = () => {
    for (let essai = 0; essai < 100; essai++) {
        const reduction = randInt(0, 1) === 0;
        const { k, tri, img } = tirerConfiguration(reduction);
        const kt = decTex(nb(k));
        const [c1, c2, c3] = shuffle(COTES);
        const perim = ratAdd(ratAdd(tri.AB, tri.AC), tri.BC);
        const perimImg = ratMul(k, perim);
        const verites = [randInt(0, 1), randInt(0, 1), randInt(0, 1), randInt(0, 1)];
        if (verites.every((v) => v === verites[0])) continue;
        // lignes chiffrées : la fausse est l'erreur nommée (÷ k au lieu de × k, ou l'inverse)
        const direct = verites[0] === 0 ? img[c1] : ratDiv(tri[c1], k);
        const origine = verites[1] === 0 ? tri[c2] : ratMul(img[c2], k);
        const types = shuffle(['formule', 'perimetre']);
        const troisieme = types[0];
        const lignes = [
            { t: `Si ${egal(c1, tri[c1])} cm, alors ${egal(IMAGE[c1], direct)} cm.`, v: verites[0], type: 'image' },
            { t: `Si ${egal(IMAGE[c2], img[c2])} cm, alors ${egal(c2, origine)} cm.`, v: verites[1], type: 'origine' },
            troisieme === 'formule'
                ? { t: verites[2] === 0 ? `On a ${tex(`${IMAGE[c3]} = k \\times ${c3}`)}.` : `On a ${tex(`${c3} = k \\times ${IMAGE[c3]}`)}.`, v: verites[2], type: 'formule' }
                : { t: `Si le périmètre du triangle ${TRI_ABC} est ${nb(perim)} cm, alors celui du triangle ${TRI_IMAGE} est ${nb(verites[2] === 0 ? perimImg : ratDiv(perim, k))} cm.`, v: verites[2], type: 'perimetre' },
            verites[3] === 0
                ? { t: `Les longueurs du triangle ${TRI_IMAGE} sont proportionnelles à celles du triangle ${TRI_ABC}.`, v: 0, type: 'propriete' }
                : { t: `On obtient les longueurs du triangle ${TRI_IMAGE} en ${reduction ? 'retirant' : 'ajoutant'} le même nombre de centimètres à celles du triangle ${TRI_ABC}.`, v: 1, type: 'propriete' },
        ];
        const chiffres = [direct, origine, perimImg, ratDiv(perim, k)];
        if (chiffres.some((v) => nbDecimales(v) > 2 || ratVal(v) <= 0)) continue;
        // second chemin : une ligne fausse ne dit jamais la bonne valeur
        if (verites[0] === 1 && ratEq(direct, img[c1])) continue;
        if (verites[1] === 1 && ratEq(origine, tri[c2])) continue;
        const melange = shuffle(lignes);
        const fig = figure(reduction, k);
        const payload = makeVraiFaux(melange.map((l) => l.t), melange.map((l) => l.v),
            `<p>Le triangle ${TRI_IMAGE} est ${nature(reduction)} du triangle ${TRI_ABC} de coefficient ${tex(`k = ${kt}`)}.</p>`
            + PAS_A_L_ECHELLE
            + RAPPEL_BROUILLON
            + '<p><b>Pour chaque affirmation, dis si elle est vraie ou fausse.</b></p>',
            { ...opts(L_AGRANDISSEMENT), elements_additionnels: fig.el });
        payload._meta.params = { reduction, k: nb(k), vraies: lignes.filter((l) => l.v === 0).map((l) => l.type), ratioDessin: fig.ratio };
        return payload;
    }
    throw new Error('gen_174 V/F : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// 6. RELIER — longueur ↔ longueur de l'image, deux leurres
// ═══════════════════════════════════════════════════════════

const genLongueurRelier = () => {
    for (let essai = 0; essai < 150; essai++) {
        const reduction = randInt(0, 1) === 0;
        const k = R(randomChoice(reduction ? K_REDUCTION : K_AGRANDISSEMENT));
        const sources = shuffle([2, 3, 4, 5, 6, 8, 9, 10, 12]).slice(0, 3).map(ratN);
        if (ratEq(k, R('1,5')) && sources.some((s) => ratEq(s, ratN(7)))) continue;
        const images = sources.map((s) => ratMul(k, s));
        if (images.some((v) => nbDecimales(v) > 2)) continue;
        // leurres : additif_rapport (s ± k) et coefficient_inverse (s ÷ k), sources différentes
        const [i1, i2] = shuffle([0, 1, 2]).slice(0, 2);
        const additif = reduction ? ratSub(sources[i1], k) : ratAdd(sources[i1], k);
        let second = { v: ratDiv(sources[i2], k), code: 'coefficient_inverse' };
        if (nbDecimales(second.v) > 2) second = { v: reduction ? ratSub(sources[i2], k) : ratAdd(sources[i2], k), code: 'additif_rapport' };
        const leurres = [{ v: additif, code: 'additif_rapport' }, second];
        const cible = (v) => `${nb(v)} cm`;
        const tous = [...images, ...leurres.map((l) => l.v)];
        if (leurres.some((l) => ratVal(l.v) <= 0 || nbDecimales(l.v) > 2)) continue;
        if (new Set(tous.map(cible)).size !== 5) continue;
        if (leurres.some((l) => sources.some((s) => ratEq(s, l.v)))) continue;        // jamais l'étiquette d'une source
        const mini = Math.min(...images.map(ratVal)); const maxi = Math.max(...images.map(ratVal));
        if (!leurres.some((l) => ratVal(l.v) > mini && ratVal(l.v) < maxi)) continue;  // relier par le rang ne marche pas
        images.forEach((v, i) => { if (!ratEq(ratDiv(v, sources[i]), k)) throw new Error('gen_174 relier : incohérent'); });
        const pairs = sources.map((s, i) => ({ source: cible(s), target: cible(images[i]) }));
        const cibles = leurres.map((l) => ({ target: cible(l.v), code: l.code }));
        const payload = makeRelier(shuffle(pairs),
            `<p>Une figure est ${reduction ? 'réduite' : 'agrandie'} avec le coefficient ${tex(`k = ${decTex(nb(k))}`)}.</p>`
            + RAPPEL_BROUILLON
            + '<p><b>Relie chaque longueur de la figure à la longueur correspondante sur l\'image. Deux longueurs sont en trop.</b></p>',
            { ...opts(L_AGRANDISSEMENT), cibles_leurres: cibles });
        payload._meta.params = {
            reduction, k: nb(k), sources: sources.map(nb),
            distracteurs: Object.fromEntries(cibles.map((l) => [l.target, l.code])),
        };
        return payload;
    }
    throw new Error('gen_174 relier : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    rapport_texte: genRapportSaisie,
    longueur_texte: genLongueurSaisie,
    agrandissement_qcm_unique: genAgrandissementQcm,
    tableau_texte_trou: genTableauTrou,
    geo_tableau_vf: genGeoVF,
    longueur_relier: genLongueurRelier,
};

export const CATEGORIES = [
    {
        id: 'agrandissement',
        label: L_AGRANDISSEMENT,
        formatsList: [
            { key: 'rapport_texte', label: 'Coefficient (saisie)' },
            { key: 'longueur_texte', label: 'Longueur manquante (saisie)' },
            { key: 'agrandissement_qcm_unique', label: 'Coefficient ou longueur (QCM)' },
            { key: 'tableau_texte_trou', label: 'Tableau de longueurs' },
            { key: 'geo_tableau_vf', label: 'Vrai/Faux' },
            { key: 'longueur_relier', label: 'Relier' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
